/**
 * Validation Toast
 *
 * Centered screen notification for validation errors.
 * Fades in, stays for duration, fades out. Click to dismiss.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, X } from 'lucide-react';
import { ToastVariant } from '../../context/ToastContext';

interface ValidationToastProps {
  message: string | null;
  onDismiss: () => void;
  duration?: number;
  variant?: ToastVariant;
}

export const ValidationToast: React.FC<ValidationToastProps> = ({
  message,
  onDismiss,
  duration = 3000,
  variant = 'error',
}) => {
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(onDismiss, 300);
  }, [onDismiss]);

  useEffect(() => {
    if (message) {
      setVisible(true);
      const timer = setTimeout(dismiss, duration);
      return () => clearTimeout(timer);
    }
  }, [message, duration, dismiss]);

  if (!message) return null;

  const Icon = variant === 'success' ? CheckCircle : AlertCircle;

  return (
    <div
      className={`validation-toast validation-toast--${variant} ${visible ? 'show' : ''}`}
      onClick={dismiss}
      role={variant === 'success' ? 'status' : 'alert'}
    >
      <Icon size={18} className="toast-icon" />
      <span>{message}</span>
      <X size={16} className="toast-dismiss" />
    </div>
  );
};
